import React, { useEffect, useState } from "react";
import { Button, Card, Form, Table } from "react-bootstrap";
import { BiTrash } from "react-icons/bi";

export interface AdditionalCharge {
  price: string;
  discount: string;
  discount_type: "%" | "₹";
  total: number;
  remark?: string;
  name?: string;
}

export default function AdditionalChargesSection({
  assemblyCharges,
  otherCharges,
  setAssemblyCharges,
  setOtherCharges,
  onValidationChange,
}: {
  assemblyCharges: AdditionalCharge;
  otherCharges: AdditionalCharge[];
  setAssemblyCharges: React.Dispatch<React.SetStateAction<AdditionalCharge>>;
  setOtherCharges: React.Dispatch<React.SetStateAction<AdditionalCharge[]>>;
  onValidationChange?: (isValid: boolean, errors: string[]) => void;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(timeout);
  }, []);

  const calculateTotal = (c: AdditionalCharge): number => {
    const price = Math.max(Number(c.price) || 0, 0);
    const discount = Math.max(Number(c.discount) || 0, 0);
    const discountVal =
      c.discount_type === "%" ? (price * discount) / 100 : discount;
    return Math.max(price - discountVal, 0);
  };

  const handleAssemblyChange = (field: keyof AdditionalCharge, value: any) => {
    if (field === "price" || field === "discount") {
      value = Math.max(Number(value) || 0, 0);
    }
    const updated = { ...assemblyCharges, [field]: value };
    updated.total = calculateTotal(updated);
    setAssemblyCharges(updated);
  };

  const handleOtherChargeChange = (
    index: number,
    field: keyof AdditionalCharge,
    value: any
  ) => {
    if (field === "price" || field === "discount") {
      value = Math.max(Number(value) || 0, 0);
    }
    const updatedCharges = [...otherCharges];
    updatedCharges[index] = { ...updatedCharges[index], [field]: value };
    updatedCharges[index].total = calculateTotal(updatedCharges[index]);
    setOtherCharges(updatedCharges);
  };

  const handleRemoveAssembly = () => {
    setAssemblyCharges({
      price: "",
      discount: "",
      discount_type: "%",
      total: 0,
    });
  };

  const handleRemoveOtherCharge = (index: number) => {
    setOtherCharges(otherCharges.filter((_, i) => i !== index));
  };

  const handleAddOtherCharge = () => {
    setOtherCharges([
      ...otherCharges,
      {
        price: "",
        discount: "",
        discount_type: "%",
        total: 0,
        remark: "",
        name: "",
      },
    ]);
  };

  const getNameError = (charge: AdditionalCharge): string => {
    if (Number(charge.price) > 0 && !charge.name?.trim()) {
      return "Name is required when price is entered.";
    }
    return "";
  };

  const validateAdditionalCharges = React.useCallback((): {
    isValid: boolean;
    errors: string[];
  } => {
    const errors: string[] = [];
    otherCharges.forEach((charge, index) => {
      if (Number(charge.price) > 0 && !charge.name?.trim()) {
        errors.push(
          `Other charge ${index + 1} name is required when price is entered.`
        );
      }
    });
    return { isValid: errors.length === 0, errors };
  }, [otherCharges]);

  useEffect(() => {
    if (onValidationChange) {
      const validation = validateAdditionalCharges();
      onValidationChange(validation.isValid, validation.errors);
    }
  }, [validateAdditionalCharges, onValidationChange]);

  return (
    <div
      className="m-0 bgback p-2 rounded-3"
      style={{
        transition: "all 0.2s ease",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(8px)",
      }}
    >
      <Card
        className="shadow-sm p-3 border-0"
        style={{
          borderRadius: "16px",
          background: "#f8fafc",
        }}
      >
        <div className="table-responsive">
          <Table
            bordered
            hover
            className="align-middle mb-0 text-nowrap additional-charges-table"
            style={{
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <thead
              style={{
                backgroundColor: "#0070b6",
                color: "#fff",
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
              }}
            >
              <tr>
                <th style={{ color: "#fff" }}>Charge</th>
                <th style={{ color: "#fff" }}>Price</th>
                <th style={{ color: "#fff" }}>Discount</th>
                <th style={{ color: "#fff" }}>Type</th>
                <th style={{ color: "#fff" }}>Total</th>
                <th style={{ color: "#fff" }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {/* Assembly Charges */}
              <tr
                style={{
                  background: "#e9f4ff",
                }}
              >
                <td className="fw-semibold text-nowrap">Assembly Charges</td>
                <td style={{ minWidth: "90px" }}>
                  <Form.Control
                    type="number"
                    min="0"
                    step="1"
                    className="form-control-sm rounded-pill shadow-sm"
                    placeholder="Price"
                    value={assemblyCharges.price}
                    onChange={(e) =>
                      handleAssemblyChange("price", parseInt(e.target.value) || 0)
                    }
                    onKeyDown={(e) => {
                      if (e.key === "." || e.key === ",") {
                        e.preventDefault();
                      }
                    }}
                  />
                </td>
                <td style={{ minWidth: "90px" }}>
                  <Form.Control
                    type="number"
                    min="0"
                    className="form-control-sm rounded-pill shadow-sm"
                    placeholder="Discount"
                    value={assemblyCharges.discount}
                    onChange={(e) =>
                      handleAssemblyChange("discount", Number(e.target.value))
                    }
                  />
                </td>
                <td style={{ minWidth: "90px" }}>
                  <Form.Select
                    className="form-select-sm rounded-pill shadow-sm"
                    value={assemblyCharges.discount_type}
                    onChange={(e) =>
                      handleAssemblyChange("discount_type", e.target.value)
                    }
                  >
                    <option value="%">%</option>
                    <option value="₹">₹</option>
                  </Form.Select>
                </td>
                <td className="fw-semibold text-success">
                  ₹ {assemblyCharges.total}
                </td>
                <td>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="rounded-circle shadow-sm"
                    onClick={handleRemoveAssembly}
                  >
                    <BiTrash />
                  </Button>
                </td>
              </tr>

              {/* Other Charges */}
              {otherCharges.map((charge, index) => {
                const nameError = getNameError(charge);
                return (
                  <tr
                    key={index}
                    style={{
                      background: "#fff",
                      borderLeft: "4px solid #0070b6",
                    }}
                  >
                    {/* Name + Remarks */}
                    <td style={{ verticalAlign: "top" }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                        }}
                      >
                        <div>
                          <small className="fw-semibold text-secondary">
                            Other Charges {index + 1}
                          </small>
                          <Form.Control
                            type="text"
                            className="form-control-sm rounded-pill shadow-sm"
                            // placeholder="Enter name"
                            placeholder="Enter name (e.g., Delivery, Packaging)"
                            value={charge.name}
                            onChange={(e) =>
                              handleOtherChargeChange(
                                index,
                                "name",
                                e.target.value
                              )
                            }
                            isInvalid={!!nameError}
                          />
                          {nameError && (
                            <div className="text-danger small mt-1">
                              {nameError}
                            </div>
                          )}
                        </div>

                        <Form.Control
                          type="text"
                          className="form-control-sm rounded-pill shadow-sm"
                          placeholder="Remarks (optional)"
                          value={charge.remark}
                          onChange={(e) =>
                            handleOtherChargeChange(
                              index,
                              "remark",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </td>

                    {/* Price */}
                    <td>
                      <Form.Control
                        type="number"
                        min="0"
                        step="1"
                        className="form-control-sm rounded-pill shadow-sm"
                        placeholder="Enter price"
                        value={charge.price}
                        onChange={(e) =>
                          handleOtherChargeChange(
                            index,
                            "price",
                            parseInt(e.target.value) || 0
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === "." || e.key === ",") {
                            e.preventDefault();
                          }
                        }}
                      />
                    </td>

                    {/* Discount */}
                    <td>
                      <Form.Control
                        type="number"
                        className="form-control-sm rounded-pill shadow-sm"
                        placeholder="Discount"
                        value={charge.discount}
                        onChange={(e) =>
                          handleOtherChargeChange(
                            index,
                            "discount",
                            Number(e.target.value)
                          )
                        }
                      />
                    </td>

                    {/* Discount Type */}
                    <td>
                      <Form.Select
                        className="form-select-sm rounded-pill shadow-sm"
                        value={charge.discount_type}
                        onChange={(e) =>
                          handleOtherChargeChange(
                            index,
                            "discount_type",
                            e.target.value
                          )
                        }
                      >
                        <option value="%">%</option>
                        <option value="₹">₹</option>
                      </Form.Select>
                    </td>

                    {/* Total */}
                    <td className="fw-semibold text-success text-nowrap">
                      ₹ {charge.total}
                    </td>

                    {/* Action */}
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="rounded-circle shadow-sm"
                        onClick={() => handleRemoveOtherCharge(index)}
                      >
                        <BiTrash />
                      </Button>
                    </td>
                  </tr>
                );
              })}

              {/* Add Button */}
              <tr>
                <td colSpan={6} className="text-center">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="rounded-pill shadow-sm px-4 mt-2"
                    onClick={handleAddOtherCharge}
                  >
                    + Add Other Charge
                  </Button>
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
